// Compact prefixes (core:, belonging:, adr:, …) and the namespaces they stand
// for. The catalog seeded to each Pod declares its own (the @prefix lines of
// the pack's Turtle, merged with core's). The built-in map below is what the code
// itself writes and queries with: SolidShare's core and the standard
// vocabularies, never an institution's own.

export const BUILT_IN_PREFIXES = Object.freeze({
  core: 'https://excentric-demo.waag.org/schema/core#',
  schema: 'https://schema.org/',
  dcterms: 'http://purl.org/dc/terms/',
  qb: 'http://purl.org/linked-data/cube#',
  prov: 'http://www.w3.org/ns/prov#',
  skos: 'http://www.w3.org/2004/02/skos/core#',
  xsd: 'http://www.w3.org/2001/XMLSchema#',
  void: 'http://rdfs.org/ns/void#',
  ldp: 'http://www.w3.org/ns/ldp#',
});

export const prefixMap = (declared = {}) => ({ ...BUILT_IN_PREFIXES, ...(declared ?? {}) });

// CURIE ↔ IRI with one prefix map. Shortening takes the longest namespace an
// IRI starts with; anything else (a literal, an unknown IRI) is left as it is.
export function curieTools(prefixes = prefixMap()) {
  const entries = Object.entries(prefixes).sort((a, b) => b[1].length - a[1].length);
  const expand = (curie) => {
    const match = /^([A-Za-z][\w-]*):(.*)$/.exec(String(curie ?? ''));
    return match && prefixes[match[1]] && !match[2].startsWith('//') ? prefixes[match[1]] + match[2] : curie;
  };
  const short = (iri) => {
    const s = String(iri ?? '');
    const hit = entries.find(([, ns]) => s.startsWith(ns));
    return hit ? `${hit[0]}:${s.slice(hit[1].length)}` : s;
  };
  return { expand, short };
}

// The prefixes a Turtle body uses as CURIEs. Quoted literals and <IRIs> are
// skipped, so "19:54" or <https://…> never count as one.
export function usedPrefixes(body) {
  const bare = String(body).replace(/"(?:[^"\\]|\\.)*"/g, '""').replace(/<[^>]*>/g, '<>');
  return new Set([...bare.matchAll(/(?<![\w:#/.-])([A-Za-z][\w-]*):(?=[A-Za-z_\d])/g)].map((m) => m[1]));
}

// The @prefix lines for a Turtle document: exactly the prefixes its body uses,
// from `prefixes`. A prefix used but known nowhere is an error here rather than
// an unreadable file on the Pod.
export function turtlePrefixLines(body, prefixes = prefixMap()) {
  const used = [...usedPrefixes(body)].sort();
  const unknown = used.filter((p) => !prefixes[p]);
  if (unknown.length) throw new Error(`No namespace declared for prefix ${unknown.map((p) => `"${p}:"`).join(', ')}`);
  return used.map((p) => `@prefix ${p}: <${prefixes[p]}> .`);
}
